import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NetworkStatus, useMutation, useQuery } from "@apollo/client";
import { Alert } from "@mui/material";
import { ConfirmDialog, toast } from "@outpave/ds";
import CustomSkeleton from "components/common/CustomSkeleton";
import { GET_COMPANY_PLANS } from "graphql/platform";
import { UPDATE_COMPANY_PLANS } from "graphql/company";
import {
  PlanChange,
  PlanPickerFields,
  usePlanSelection,
} from "./PlanPickerDrawer";

const NO_PLANS: string[] = [];

/**
 * One company's apps and add-ons on its Plans tab; each switch saves as it flips. The switches mount only once the
 * saved plans are known, so the first one flipped can never save over them, and they rest while a save and its
 * refetch run, so two quick flips cannot land out of order.
 */
const CompanyPlansPanel: React.FC<{ companyId: string }> = ({ companyId }) => {
  const { t } = useTranslation();
  const { data, error, refetch, networkStatus } = useQuery(GET_COMPANY_PLANS, {
    variables: { companyId },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const appPlanIds: string[] = useMemo(
    () => data?.companyPlans?.appPlanIds ?? NO_PLANS,
    [data],
  );
  const addOnPlanIds: string[] = useMemo(
    () => data?.companyPlans?.addOnPlanIds ?? NO_PLANS,
    [data],
  );

  if (!data) {
    return error ? (
      <Alert severity="error" role="status">
        {t("AccountPlansLoadError")}
      </Alert>
    ) : (
      <CustomSkeleton />
    );
  }

  return (
    <CompanyPlanSwitches
      companyId={companyId}
      appPlanIds={appPlanIds}
      addOnPlanIds={addOnPlanIds}
      refreshing={networkStatus === NetworkStatus.refetch}
      onSaved={() => void refetch()}
    />
  );
};

const CompanyPlanSwitches: React.FC<{
  companyId: string;
  appPlanIds: string[];
  addOnPlanIds: string[];
  refreshing: boolean;
  onSaved: () => void;
}> = ({ companyId, appPlanIds, addOnPlanIds, refreshing, onSaved }) => {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PlanChange | null>(null);
  const [updateCompanyPlans, { loading: saving }] = useMutation(
    UPDATE_COMPANY_PLANS,
    {
      onCompleted: () => {
        toast.success(t("planUpdated"));
        onSaved();
      },
      onError: (error) => {
        toast.error(error?.message || t("failedToUpdatePlans"));
        selection.reset();
      },
    },
  );

  const save = (change: PlanChange) =>
    updateCompanyPlans({
      variables: {
        companyId,
        input: {
          appPlanIds: change.appPlanIds,
          addOnPlanIds: change.addOnPlanIds,
        },
      },
    });

  // Turning an app off also turns off the add-ons that need it, so that one asks first.
  const selection = usePlanSelection(
    appPlanIds,
    addOnPlanIds,
    true,
    (change) =>
      change.cascadedAddOnIds.length ? setPending(change) : save(change),
  );

  return (
    <>
      <PlanPickerFields
        selection={selection}
        live
        disabled={saving || refreshing}
      />
      <ConfirmDialog
        open={!!pending}
        tone="danger"
        title={t("TurnOffAppTitle")}
        message={t("TurnOffAppMessage", {
          addOns: (pending?.cascadedAddOnIds ?? [])
            .map(selection.planName)
            .join(", "),
          interpolation: { escapeValue: false },
        })}
        confirmLabel={t("Turn off")}
        cancelLabel={t("Cancel")}
        busy={saving}
        onConfirm={() => {
          if (pending) save(pending);
          setPending(null);
        }}
        onCancel={() => {
          selection.reset();
          setPending(null);
        }}
      />
    </>
  );
};

export default CompanyPlansPanel;
